import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import FilterPanel, { type CategoryFilterState } from "./components/FilterPanel";
import { filterWorks } from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";
import type { WorkType } from "@/types/media";

const PAGE_SIZE = 4;

const defaultFilter: CategoryFilterState = {
  regions: [],
  years: [],
  genres: [],
  minRating: 0,
  sort: "hot",
};

const typeMeta: Record<WorkType, { title: string; en: string; desc: string; seoTitle: string; seoDesc: string; keywords: string }> = {
  movie: {
    title: "电影分类",
    en: "Movies",
    desc: "汇集人物命运与时代光影的影片，支持按地区、年份、类型、评分多条件复合筛选。",
    seoTitle: "电影推荐 - 挣扎吧亚当君",
    seoDesc: "精选电影推荐与剧情介绍，支持按地区、年份、类型、评分多条件筛选，快速找到想看的佳片与深度解析。",
    keywords: "电影推荐,电影剧情介绍,电影筛选,挣扎吧亚当君",
  },
  tv: {
    title: "电视剧分类",
    en: "TV Series",
    desc: "从单集结构到群像叙事，按分集线索与类型标签快速定位你的下一部追剧。",
    seoTitle: "电视剧推荐 - 挣扎吧亚当君",
    seoDesc: "精选电视剧推荐与分集剧情导航，支持按地区、年份、类型、评分多条件筛选，找到值得追的好剧。",
    keywords: "电视剧推荐,剧集剧情介绍,追剧导航,挣扎吧亚当君",
  },
};

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const workType: WorkType = type === "tv" ? "tv" : "movie";
  const meta = typeMeta[workType];

  useSeo({ title: meta.seoTitle, description: meta.seoDesc, keywords: meta.keywords });

  const [filter, setFilter] = useState<CategoryFilterState>(defaultFilter);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilter(defaultFilter);
    setVisible(PAGE_SIZE);
  }, [workType]);

  const results = useMemo(() => filterWorks(workType, filter), [workType, filter]);
  const shown = results.slice(0, visible);
  const hasMore = visible < results.length;

  const activeCount =
    filter.regions.length + filter.years.length + filter.genres.length + (filter.minRating ? 1 : 0);

  const reset = () => {
    setFilter(defaultFilter);
    setVisible(PAGE_SIZE);
  };

  const onChange = (next: CategoryFilterState) => {
    setFilter(next);
    setVisible(PAGE_SIZE);
  };

  const filterNode = (
    <FilterPanel type={workType} value={filter} onChange={onChange} />
  );

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: workType === "movie" ? "电影" : "电视剧" },
        ]}
      />

      <div className="mt-6 mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-[3px] rounded-full bg-accent-500"></span>
            <span className="text-xs font-label font-medium tracking-[0.2em] text-accent-700 uppercase">
              {meta.en}
            </span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">{meta.title}</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">{meta.desc}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-300/70 text-sm text-foreground-700 hover:border-primary-400 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-filter-3-line"></i>
            筛选
            {activeCount > 0 && (
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-accent-950 text-[11px] font-bold">
                {activeCount}
              </span>
            )}
          </button>
          <div className="hidden lg:block">
            <label className="flex items-center gap-2 h-10 px-4 rounded-md bg-background-50 border border-background-200/80 text-sm text-foreground-600">
              <i className="ri-sort-desc"></i>
              <select
                aria-label="排序方式"
                value={filter.sort}
                onChange={(e) => onChange({ ...filter, sort: e.target.value as CategoryFilterState["sort"] })}
                className="bg-transparent text-sm text-foreground-800 outline-none cursor-pointer"
              >
                <option value="hot">按热门度排序</option>
                <option value="year">按时间排序</option>
                <option value="rating">按评分排序</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      <div className="flex gap-6 items-start">
        <aside className="hidden lg:block w-72 shrink-0 sticky top-24">{filterNode}</aside>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-5 lg:hidden">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-950">{results.length}</strong> 部作品
            </p>
            <select
              aria-label="排序方式"
              value={filter.sort}
              onChange={(e) => onChange({ ...filter, sort: e.target.value as CategoryFilterState["sort"] })}
              className="h-9 px-3 rounded-md bg-background-50 border border-background-200/80 text-sm text-foreground-800 outline-none cursor-pointer"
            >
              <option value="hot">热门</option>
              <option value="year">时间</option>
              <option value="rating">评分</option>
            </select>
          </div>

          {results.length === 0 ? (
            <div className="py-20 text-center bg-background-50 rounded-lg border border-dashed border-background-300">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                <i className="ri-search-eye-line text-3xl"></i>
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950">没有找到符合条件的作品</h3>
              <p className="mt-2 text-sm text-foreground-500">试着放宽筛选条件，或直接重置查看全部内容。</p>
              <button
                type="button"
                onClick={reset}
                className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {shown.map((w) => (
                  <WorkCard key={w.id} work={w} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 h-11 px-8 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    加载更多（{results.length - visible}）
                  </button>
                </div>
              )}
              {!hasMore && results.length > PAGE_SIZE && (
                <p className="mt-10 text-center text-sm text-foreground-400">已经到底啦，共 {results.length} 部作品</p>
              )}
            </>
          )}
        </div>
      </div>

      {/* 移动端筛选抽屉 */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto bg-background-100 rounded-t-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭筛选"
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {filterNode}
            <div className="sticky bottom-0 mt-4 pt-4 bg-background-100 flex gap-3">
              <button
                type="button"
                onClick={reset}
                className="flex-1 h-11 rounded-md border border-background-300 text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                查看 {results.length} 个结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}